import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { RefreshCw, AlertTriangle, Globe } from "lucide-react";
import { API, EXT_STATUS_BADGE } from "./constants";

// ── External Services card ───────────────────────────────────────────────────
//
// Polls /api/superadmin/external-services every 5 min. The endpoint serves the
// cached snapshot of all 6 third-party deps (MongoDB, DO Spaces, Stripe,
// OpenSanctions, Emergent LLM, OFAC SDN). The "Verificar ahora" button calls
// /external-services/check which forces a fresh probe across every service —
// because some of them cost money, we wrap the action in a confirm dialog.

function ExternalServiceRow({ service }) {
  const conf = EXT_STATUS_BADGE[service.status] || EXT_STATUS_BADGE.fail;
  const ttlH = service.ttl_seconds >= 3600
    ? `${Math.round(service.ttl_seconds / 3600)} h`
    : `${Math.round(service.ttl_seconds / 60)} min`;
  const checkedDate = service.checked_at ? new Date(service.checked_at) : null;
  const checkedLabel = checkedDate
    ? checkedDate.toLocaleString("es-MX", { dateStyle: "short", timeStyle: "medium" })
    : "—";
  return (
    <div
      data-testid={`ext-service-row-${service.service}`}
      className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-slate-900/40"
    >
      <div className="flex items-center gap-3 min-w-0">
        <span
          className={`w-2.5 h-2.5 rounded-full ${conf.dot} shrink-0`}
          aria-hidden="true"
        />
        <div className="min-w-0">
          <p className="text-sm text-slate-100 font-medium">{service.label}</p>
          <p
            data-testid={`ext-service-message-${service.service}`}
            className="text-xs text-slate-500 mt-0.5 truncate max-w-[440px]"
            title={service.message}
          >
            {service.message}
          </p>
          <p className="text-[10px] text-slate-600 mt-0.5">
            Último check: {checkedLabel}
            {service.cached ? " · servido desde caché" : " · live"}
            {" · TTL " + ttlH}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {service.latency_ms != null && (
          <span
            data-testid={`ext-service-latency-${service.service}`}
            className="text-xs font-mono text-slate-400"
          >
            {service.latency_ms} ms
          </span>
        )}
        <span
          data-testid={`ext-service-badge-${service.service}`}
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-[11px] font-semibold ${conf.cls}`}
        >
          {conf.label}
        </span>
      </div>
    </div>
  );
}

export function ExternalServicesCard({ onSummaryChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/superadmin/external-services`,
        { withCredentials: true });
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Error al consultar servicios");
    } finally {
      setLoading(false);
    }
  }, []);

  const forceCheck = useCallback(async () => {
    try {
      setRefreshing(true);
      const res = await axios.post(`${API}/superadmin/external-services/check`,
        {}, { withCredentials: true });
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Error al verificar servicios");
    } finally {
      setRefreshing(false);
      setConfirmOpen(false);
    }
  }, []);

  useEffect(() => {
    load();
    // 5 min: matches the shortest TTL in the registry; longer-lived entries
    // are still served from cache so this poll is cheap.
    const interval = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [load]);

  // Push a compact summary up so the top banner can show a single
  // consolidated counter across internal deps + external services.
  useEffect(() => {
    if (!data || !onSummaryChange) return;
    const services = Object.values(data.services || {});
    onSummaryChange({
      ok: services.filter((s) => s.status === "ok").length,
      warn: services.filter((s) => s.status === "warn").length,
      fail: services.filter((s) => s.status === "fail").length,
      total: services.length,
      overall: data.overall,
    });
  }, [data, onSummaryChange]);

  const overall = data?.overall;
  const order = data?.service_order || [];
  const services = data?.services || {};

  const headerBadge = overall && (
    <span
      data-testid={`ext-overall-${overall}`}
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-[11px] font-semibold ${
        EXT_STATUS_BADGE[overall]?.cls || EXT_STATUS_BADGE.fail.cls
      }`}
    >
      {overall === "ok" ? "Todos OK" : overall === "warn" ? "Con advertencias" : "Con fallas"}
    </span>
  );

  return (
    <div
      data-testid="external-services-card"
      className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden"
    >
      <div className="p-5 border-b border-slate-800 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-cyan-500/10 rounded-lg shrink-0">
            <Globe className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Servicios externos</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-[560px]">
              Estado en vivo de las APIs y servicios externos. Cada check se cachea
              según su costo (5 min para infra, 24 h para APIs pagas).
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {headerBadge}
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={refreshing}
            data-testid="ext-services-verify-now-btn"
            className="px-3 py-1.5 text-xs rounded-md border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
            title="Forzar verificación de todos los servicios (puede tener costo)"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Verificar ahora
          </button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-300 bg-red-500/10 border-b border-red-500/30 px-5 py-3">
          {error}
        </div>
      )}

      {loading && !data ? (
        <div className="h-24 flex items-center justify-center text-slate-500 text-sm">
          <RefreshCw className="w-4 h-4 animate-spin mr-2" />
          Cargando estado de servicios…
        </div>
      ) : (
        <div className="divide-y divide-slate-800">
          {order.map((name) => (
            <ExternalServiceRow key={name} service={services[name]} />
          ))}
        </div>
      )}

      {/* Confirmation dialog — built inline rather than reaching for shadcn's
          AlertDialog because we already use lightweight modals elsewhere on
          the superadmin pages and want a consistent look. */}
      {confirmOpen && (
        <div
          data-testid="ext-services-confirm-overlay"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !refreshing && setConfirmOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-slate-900 border border-slate-700 rounded-xl max-w-md w-full p-6 shadow-2xl"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 bg-amber-500/10 rounded-lg shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1">
                <h4 className="text-base font-semibold text-white">
                  Confirmar verificación
                </h4>
                <p className="text-sm text-slate-400 mt-2">
                  Esto hará llamadas de prueba a servicios externos que pueden tener
                  costo (OpenSanctions, Emergent LLM). ¿Continuar?
                </p>
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={refreshing}
                data-testid="ext-services-confirm-cancel-btn"
                className="px-4 py-2 text-sm rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={forceCheck}
                disabled={refreshing}
                data-testid="ext-services-confirm-proceed-btn"
                className="px-4 py-2 text-sm rounded-md bg-cyan-500 text-slate-950 hover:bg-cyan-400 font-medium disabled:opacity-50 inline-flex items-center gap-2"
              >
                {refreshing && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                Sí, verificar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
